import styled from "@emotion/styled";
import { type ReactNode } from "react";
import { useNavigate } from "react-router";

import getAuthClient from "@skilitics/designer-auth";
import { Divider, Icon, MenuItem, Skeleton, SkiliticsIcon } from "@skilitics/designer-ui-components";
import { useFormatMessage, type FormatMessages } from "@skilitics/localization";
import { useFeatureSwitch } from "@skilitics/nautilus-core-designer";

import { useWorkspaceId, useWorkspaceListQuery, useInvalidateWorkspaceList } from "../auth/workspaces.ts";
import { NewOrgDialog } from "./NewOrgDialog";
import { MenuIconContainer } from "./UserAccountMenu";

const iconSize = 14;

type Props = {
  setNewOrgDialog: (dialog: ReactNode | null) => void;
  // Called after switching workspace (e.g. to navigate out of an open module). The sidebar omits it.
  onWorkspaceSelected?: () => Promise<void>;
  showWorkspaceHeader?: boolean;
};

export const WorkspaceMenuItems = ({ showWorkspaceHeader, setNewOrgDialog, onWorkspaceSelected }: Props) => {
  const formatMessage = useFormatMessage(formatStrings);

  // TODO: remove this feature switch once we workspace billing is implemented.
  const canCreateWorkspace = useFeatureSwitch("org-admin");

  const selectedId = useWorkspaceId();
  const { data: workspaces, isLoading } = useWorkspaceListQuery();
  const invalidateWorkspaceList = useInvalidateWorkspaceList();

  const navigate = useNavigate();

  const selectContext = async (workspace: string) => {
    await getAuthClient().switchWorkspace(workspace);
    await onWorkspaceSelected?.();
  };

  const showNewOrgDialog = () => {
    setNewOrgDialog(
      <NewOrgDialog
        create={async (name: string) => {
          await getAuthClient().createWorkspace(name);
          await invalidateWorkspaceList();
          await navigate("/settings/workspace/members");
        }}
        close={() => setNewOrgDialog(null)}
      />,
    );
  };

  return (
    <>
      {showWorkspaceHeader && (
        <MenuItem disabled>
          <span>{formatMessage("workspaceHeader")}</span>
        </MenuItem>
      )}
      {isLoading
        ? [1, 2].map((i) => (
            <MenuItem key={i} disabled>
              <MenuIconContainer />
              <Skeleton variant="text" width="70%" animation="wave" />
            </MenuItem>
          ))
        : (workspaces ?? []).map(({ id, name }) => (
            <StyledMenuItem
              key={id}
              disabled={selectedId === id}
              className={selectedId === id ? "selected" : ""}
              onClick={() => void selectContext(id)}
              data-test={`workspace-switcher-org-${id}`}
            >
              <MenuIconContainer>
                {selectedId === id && <SkiliticsIcon icon="tick" size={iconSize} />}
              </MenuIconContainer>
              <span>{name}</span>
            </StyledMenuItem>
          ))}
      {canCreateWorkspace && (
        <>
          <Divider />
          <MenuItem onClick={showNewOrgDialog} data-test="workspace-switcher-new">
            <MenuIconContainer>
              <Icon icon="plus" size={iconSize} />
            </MenuIconContainer>
            <span>{formatMessage("workspaceCreate")}</span>
          </MenuItem>
        </>
      )}
    </>
  );
};

const StyledMenuItem = styled(MenuItem)`
  &.selected {
    opacity: 1;
    background: ${({ theme }) => theme.primaryContainer};
    color: ${({ theme }) => theme.onPrimaryContainer};
  }
`;

const formatStrings = {
  workspaceHeader: {
    en: "Workspaces",
  },
  workspaceCreate: {
    en: "Create New Workspace",
  },
} satisfies FormatMessages;
